"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { budgetTabs, lightTabs, voitureTabs,
  veloTabs, disposTabs, santeTabs } from "@/app/lib/navigation";
import { createClient } from "@/lib/supabase/client";
import type { ModuleFlags } from "./app-shell";
import { ChangelogButton } from "./changelog-modal";
import { NotificationsBell } from "./notifications-bell";
import { useAccessibleModules } from "@/lib/auth/use-user-plan";

interface Band {
  id: string;
  name: string;
}

// Music sub-pages for a selected band
const musicSubPages = [
  { key: "detail", label: "Groupe", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
  { key: "repertoire", label: "Repertoire", icon: "M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" },
  { key: "patches", label: "Patches", icon: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" },
  { key: "calendrier", label: "Calendrier", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
];

function getHref(bandId: string, key: string) {
  if (key === "detail") return `/music/groupes/${bandId}`;
  return `/music/${key}?band=${bandId}`;
}

export function Sidebar({
  activeModule,
  moduleFlags = { intermittenceEnabled: false, portefeuilleEnabled: false, groupeAmisEnabled: false, groupeAmisLabel: "Groupe d'amis" },
}: {
  activeModule: "budget" | "music" | "famille" | "rando" | "light" | "voiture" | "velo" | "dispos" | "sante";
  moduleFlags?: ModuleFlags;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [bands, setBands] = useState<Band[]>([]);
  const [selectedBand, setSelectedBand] = useState<string | null>(null);
  const [showAddBand, setShowAddBand] = useState(false);
  const [newBandName, setNewBandName] = useState("");
  const [creating, setCreating] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Liste des groupes d'amis / vie commune (sous-menu "Groupes" du module Budget).
  const [amisGroupes, setAmisGroupes] = useState<{ id: string; nom: string; mode?: string }[]>([]);
  /** Groupes du module Dispos, listés dans sa sidebar. */
  const [disposGroupes, setDisposGroupes] = useState<{ id: string; nom: string }[]>([]);
  // Groupe de dispos actif, porté par l'URL : c'est ce qui permet à la
  // sidebar de surligner le bon élément après une navigation.
  const searchParams = useSearchParams();
  const currentDispoGroupe = searchParams?.get("groupe") ?? null;
  // LOT 127 — Menu burger pour switcher de module (remplace l'ancien
  // toggle Budget/Music dans le sidebar).
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);

  // Check admin status
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user?.email) return;
      supabase.rpc("is_user_admin", { user_email: user.email }).then(({ data }) => {
        setIsAdmin(data === true);
      });
    });
  }, []);

  // Fetch user's bands
  useEffect(() => {
    if (activeModule !== "music") return;
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase
        .from("band_members")
        .select("band:bands(id, name)")
        .eq("user_id", user.id)
        .then(({ data }) => {
          const b = (data ?? [])
            .map((m: Record<string, unknown>) => m.band as Band | Band[])
            .flat()
            .filter(Boolean);
          setBands(b);
          // Auto-select from URL
          const match = pathname.match(/\/music\/groupes\/([^/?]+)/);
          if (match) {
            setSelectedBand(match[1]);
          } else {
            const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
            const bandParam = params.get("band");
            if (bandParam) setSelectedBand(bandParam);
            else if (b.length > 0 && !selectedBand) setSelectedBand(b[0].id);
          }
        });
    });
  }, [activeModule, pathname]);

  // Sync selectedBand when URL changes
  useEffect(() => {
    const match = pathname.match(/\/music\/groupes\/([^/?]+)/);
    if (match) setSelectedBand(match[1]);
  }, [pathname]);

  // Re-fetch quand un groupe est créé/supprimé ailleurs (page « Mes groupes »).
  useEffect(() => {
    if (activeModule !== "music") return;
    function reload() {
      const supabase = createClient();
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (!user) return;
        supabase
          .from("band_members")
          .select("band:bands(id, name)")
          .eq("user_id", user.id)
          .then(({ data }) => {
            const b = (data ?? [])
              .map((m: Record<string, unknown>) => m.band as Band | Band[])
              .flat()
              .filter(Boolean);
            setBands(b);
            setSelectedBand((cur) => (cur && !b.some((x) => x.id === cur) ? null : cur));
          });
      });
    }
    window.addEventListener("gestic:bands-changed", reload);
    return () => window.removeEventListener("gestic:bands-changed", reload);
  }, [activeModule]);

  // Fetch des groupes d'amis / vie commune (sous-menu "Groupes" du Budget).
  useEffect(() => {
    if (activeModule !== "budget" || !moduleFlags.groupeAmisEnabled) {
      setAmisGroupes([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: mems } = await supabase
        .from("groupes_amis_membres")
        .select("groupe_id")
        .eq("user_id", user.id)
        .eq("status", "accepted");
      const ids = (mems ?? []).map((m) => m.groupe_id as string);
      if (ids.length === 0) { if (!cancelled) setAmisGroupes([]); return; }
      let rows: Record<string, unknown>[] = [];
      const withMode = await supabase
        .from("groupes_amis")
        .select("id, nom, mode")
        .in("id", ids)
        .order("created_at");
      if (withMode.error && /mode/.test(withMode.error.message)) {
        const noMode = await supabase
          .from("groupes_amis")
          .select("id, nom")
          .in("id", ids)
          .order("created_at");
        rows = (noMode.data ?? []) as Record<string, unknown>[];
      } else {
        rows = (withMode.data ?? []) as Record<string, unknown>[];
      }
      if (!cancelled) {
        setAmisGroupes(rows.map((g) => ({
          id: g.id as string,
          nom: (g.nom as string) ?? "Groupe",
          mode: g.mode as string | undefined,
        })));
      }
    };
    void load();
    // Rafraîchit en live à la création d'un groupe (event émis par amis-client).
    const onChanged = () => void load();
    window.addEventListener("gestic:groupes-changed", onChanged);
    return () => { cancelled = true; window.removeEventListener("gestic:groupes-changed", onChanged); };
  }, [activeModule, moduleFlags.groupeAmisEnabled, pathname]);

  // Groupes de disponibilités — listés directement dans la sidebar du
  // module, pour passer de l'un à l'autre sans repasser par la page.
  useEffect(() => {
    if (activeModule !== "dispos") { setDisposGroupes([]); return; }
    let cancelled = false;
    const load = async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from("dispo_groupes").select("id, nom").order("created_at");
      if (!cancelled) {
        setDisposGroupes(((data ?? []) as Record<string, unknown>[])
          .map((g) => ({ id: g.id as string, nom: (g.nom as string) ?? "Groupe" })));
      }
    };
    void load();
    // Rafraîchit à la création/suppression d'un groupe (événement émis par
    // dispos-client) : sans ça la sidebar resterait sur une liste périmée.
    const onChanged = () => void load();
    window.addEventListener("gestic:dispos-changed", onChanged);
    return () => { cancelled = true; window.removeEventListener("gestic:dispos-changed", onChanged); };
  }, [activeModule, pathname]);

  async function handleCreateBand() {
    if (!newBandName.trim()) return;
    setCreating(true);
    const supabase = createClient();
    // created_by est requis (NOT NULL + RLS auth.uid() = created_by) ;
    // le créateur est ajouté en admin par le trigger on_band_created.
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setCreating(false); return; }
    const { data, error } = await supabase
      .from("bands")
      .insert({ name: newBandName.trim(), created_by: user.id })
      .select("id, name")
      .single();
    if (data && !error) {
      setBands((prev) => [...prev, data]);
      setSelectedBand(data.id);
      setNewBandName("");
      setShowAddBand(false);
      router.push(`/music/groupes/${data.id}`);
    } else {
      console.error("Error creating band (sidebar):", error);
      alert(`Création du groupe impossible : ${error?.message ?? "erreur inconnue"}`);
    }
    setCreating(false);
  }

  const selectedBandObj = bands.find((b) => b.id === selectedBand);

  const { intermittenceEnabled, portefeuilleEnabled, groupeAmisEnabled, groupeAmisLabel } = moduleFlags;
  // LOT 248 — Sub-modules de Revenus (Intermittence / Clients).
  // LOT 252 — Groupe d'amis n'est PAS un sub-module : il est top-level
  // (rendu séparément dans la liste principale, pas imbriqué).
  const hasSubModules = intermittenceEnabled || portefeuilleEnabled;

  // Budget mode
  if (activeModule === "budget") {
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader
          activeModule={activeModule}
          menuOpen={moduleMenuOpen}
          setMenuOpen={setModuleMenuOpen}
        />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {/* LOT 116 — Sur desktop, on conserve l'arbo sub-modules
              imbriqués sous "Revenus". On filtre donc les nouveaux
              tabs subModule (Intermittence/Clients) que la
              `bottom-tab-bar` mobile rend en flat.
              LOT 252 — Le module optionnel "Groupe d'amis" est top-level
              mais conditionnel : on skip s'il n'est pas activé. */}
          {budgetTabs.filter((tab) => {
            if (tab.subModule) return false;
            // Le module "Groupe d'amis / Vie commune" n'a plus de ligne dédiée :
            // on clique directement sur un groupe dans la section "Groupes"
            // rendue plus bas. On retire donc toujours cet onglet.
            if (tab.optionalModule === "groupeAmis") return false;
            return true;
          }).map((tab) => {
            const isActive = tab.href === "/budget" ? pathname === tab.href : pathname.startsWith(tab.href);
            // Revenus : enlever le highlight si on est sur un sous-module
            const isRevenuActive = tab.href === "/budget/revenus"
              ? pathname === "/budget/revenus"
              : isActive;
            return (
              <div key={tab.href}>
                <Link
                  href={tab.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isRevenuActive
                      ? "bg-forest/10 text-forest-dark dark:text-forest-light font-semibold"
                      : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                  }`}
                >
                  <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                  </svg>
                  {tab.optionalModule === "groupeAmis" ? groupeAmisLabel : tab.label}
                </Link>

                {/* Sous-modules de Revenus */}
                {tab.href === "/budget/revenus" && hasSubModules && (
                  <div className="ml-3 mt-1 mb-1 space-y-0.5">
                    {intermittenceEnabled && (
                      <SousModule
                        href="/budget/intermittence"
                        actif={pathname === "/budget/intermittence"}
                        icone={<IconeCleDeSol />}
                      >
                        Intermittence
                      </SousModule>
                    )}
                    {portefeuilleEnabled && (
                      <SousModule
                        href="/budget/portefeuille"
                        actif={pathname === "/budget/portefeuille"}
                        icone={
                          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                          </svg>
                        }
                      >
                        Clients
                      </SousModule>
                    )}
                    {/* LOT 252 — Groupe d'amis n'est PLUS imbriqué sous Revenus :
                        il est top-level (cf. rendu plus haut dans le map principal
                        via tab.optionalModule). */}
                  </div>
                )}

              </div>
            );
          })}

          {/* Section "Groupes" (remplace l'ancienne ligne de module) :
              en-tête non cliquable + chaque groupe en alinéa vers la droite. */}
          {groupeAmisEnabled && (
            <div className="pt-2">
              <p className="px-3 pb-0.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted/70 select-none">
                Groupes
              </p>
              <div className="ml-3 mt-1 space-y-0.5">
                {amisGroupes.map((g) => (
                  <SousLien
                    key={g.id}
                    href={`/budget/amis?groupe=${g.id}`}
                    actif={false}
                    couleur={g.mode === "vie_commune" ? "#2D9D5C" : "#F59E0B"}
                    icone={g.mode === "vie_commune"
                      ? "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      : "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"}
                  >
                    {g.nom}
                  </SousLien>
                ))}
                <SousLien href="/budget/amis?create=1" actif={false} couleur="#9CA3AF" icone="M12 4v16m8-8H4" discret>
                  Créer un groupe
                </SousLien>
              </div>
            </div>
          )}
        </nav>
        {isAdmin && (
          <div className="px-3 pb-2">
            <Link href="/admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${pathname === "/admin" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold" : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"}`}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
              Admin
            </Link>
          </div>
        )}
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button
            onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }}
            className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors"
            title="Se deconnecter"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  // LOT 217 — Famille mode (1 seul "page" : l'arbre)
  if (activeModule === "famille") {
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader
          activeModule={activeModule}
          menuOpen={moduleMenuOpen}
          setMenuOpen={setModuleMenuOpen}
        />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          <Link
            href="/famille"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium bg-violet-500/10 text-violet-700 dark:text-violet-300 font-semibold"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4m-4-4v4m8-4v4M3 10h18M5 10v10a2 2 0 002 2h10a2 2 0 002-2V10M9 14a3 3 0 116 0" />
            </svg>
            L&apos;arbre
          </Link>
          <p className="px-3 mt-4 text-[11px] text-text-muted leading-snug">
            Pinch / molette pour zoom · Glisse pour déplacer · Tape sur une
            personne pour modifier
          </p>
        </nav>
        {isAdmin && (
          <div className="px-3 pb-2">
            <Link href="/admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${pathname === "/admin" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold" : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"}`}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
              Admin
            </Link>
          </div>
        )}
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button
            onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }}
            className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors"
            title="Se deconnecter"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  // Module Vélo : page unique (fiche + pièces + interventions + km)
  if (activeModule === "dispos") {
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader activeModule={activeModule} menuOpen={moduleMenuOpen} setMenuOpen={setModuleMenuOpen} />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {disposTabs.map((tab) => {
            const isActive = pathname === tab.href && !currentDispoGroupe;
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-forest/10 text-forest font-semibold"
                    : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                </svg>
                {tab.label}
              </Link>
            );
          })}

          {/* Groupes — accès direct depuis la sidebar. Le groupe actif est
              porté par ?groupe= : la page le lit au chargement. */}
          {disposGroupes.length > 0 && (
            <div className="pt-3">
              <p className="px-3 pb-1 text-[10px] uppercase tracking-wider text-text-muted">Groupes</p>
              {disposGroupes.map((g) => {
                const isActive = currentDispoGroupe === g.id;
                return (
                  <Link
                    key={g.id}
                    href={`/dispos?groupe=${g.id}`}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all ${
                      isActive
                        ? "bg-forest/10 text-forest font-semibold"
                        : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-forest/60 shrink-0" aria-hidden />
                    <span className="truncate">{g.nom}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </nav>
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }} className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors" title="Se deconnecter">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  if (activeModule === "velo") {
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader activeModule={activeModule} menuOpen={moduleMenuOpen} setMenuOpen={setModuleMenuOpen} />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {veloTabs.map((tab) => {
            const isActive = pathname === tab.href || (tab.href !== "/velo" && pathname.startsWith(tab.href));
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold"
                    : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                </svg>
                {tab.label}
              </Link>
            );
          })}
        </nav>
        {isAdmin && (
          <div className="px-3 pb-2">
            <Link href="/admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${pathname === "/admin" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold" : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"}`}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
              Admin
            </Link>
          </div>
        )}
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }} className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors" title="Se deconnecter">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  // Module Santé : page unique (timeline + RDV + suivis + dossiers)
  if (activeModule === "sante") {
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader activeModule={activeModule} menuOpen={moduleMenuOpen} setMenuOpen={setModuleMenuOpen} />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {santeTabs.map((tab) => {
            const isActive = pathname === tab.href || (tab.href !== "/sante" && pathname.startsWith(tab.href));
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-rose-500/10 text-rose-700 dark:text-rose-300 font-semibold"
                    : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                </svg>
                {tab.label}
              </Link>
            );
          })}
        </nav>
        {isAdmin && (
          <div className="px-3 pb-2">
            <Link href="/admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${pathname === "/admin" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold" : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"}`}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
              Admin
            </Link>
          </div>
        )}
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }} className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors" title="Se deconnecter">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  // Module Rando : matos, randos, groupes, share
  if (activeModule === "voiture") {
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader activeModule={activeModule} menuOpen={moduleMenuOpen} setMenuOpen={setModuleMenuOpen} />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {voitureTabs.map((tab) => {
            const isActive = pathname === tab.href || (tab.href !== "/voiture" && pathname.startsWith(tab.href));
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-sky-500/10 text-sky-700 dark:text-sky-300 font-semibold"
                    : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                </svg>
                {tab.label}
              </Link>
            );
          })}
        </nav>
        {isAdmin && (
          <div className="px-3 pb-2">
            <Link href="/admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${pathname === "/admin" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold" : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"}`}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
              Admin
            </Link>
          </div>
        )}
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }} className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors" title="Se deconnecter">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  if (activeModule === "rando") {
    const RANDO_PAGES = [
      { href: "/rando", label: "Vue d'ensemble", icon: "M3 12l9-9 9 9M5 10v10h14V10" },
      { href: "/rando/matos", label: "Mon matos", icon: "M20 7L9 18l-5-5" }, // placeholder, icône sac à dos plus bas
      { href: "/rando/nourriture", label: "Garde-manger", icon: "M5 3h14l-1 7a5 5 0 01-12 0L5 3zm0 18h14" },
      { href: "/rando/routes", label: "Mes randos", icon: "M3 21l4-7 5 4 4-6 5 9M3 21h18" },
      { href: "/rando/groupes", label: "Groupes", icon: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" },
    ];
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader
          activeModule={activeModule}
          menuOpen={moduleMenuOpen}
          setMenuOpen={setModuleMenuOpen}
        />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {RANDO_PAGES.map((p) => {
            const isActive = pathname === p.href || (p.href !== "/rando" && pathname.startsWith(p.href));
            return (
              <Link
                key={p.href}
                href={p.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold"
                    : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={p.icon} />
                </svg>
                {p.label}
              </Link>
            );
          })}
        </nav>
        {isAdmin && (
          <div className="px-3 pb-2">
            <Link href="/admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${pathname === "/admin" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold" : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"}`}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
              Admin
            </Link>
          </div>
        )}
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button
            onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }}
            className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors"
            title="Se deconnecter"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  // REFONTE v2 — Gestic Light : module mono-page (vue d'ensemble).
  if (activeModule === "light") {
    return (
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
        <BrandHeader
          activeModule={activeModule}
          menuOpen={moduleMenuOpen}
          setMenuOpen={setModuleMenuOpen}
        />
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {/* Groupe d'amis : même réglage que Budget Pro (toggle dans les
              Réglages Light) — l'onglet n'apparaît que si le module est actif. */}
          {lightTabs
            .filter((tab) => tab.href !== "/light/amis" || groupeAmisEnabled)
            .filter((tab) => tab.href !== "/light/repartition" || !!moduleFlags.repartitionEnabled)
            .map((tab) => {
            const isActive = tab.href === "/light" ? pathname === "/light" : pathname.startsWith(tab.href);
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? "bg-forest/10 text-forest dark:text-forest-light font-semibold"
                    : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                }`}
              >
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                </svg>
                {tab.href === "/light/amis" ? groupeAmisLabel : tab.label}
              </Link>
            );
          })}
          <p className="px-3 mt-4 text-[11px] text-text-muted leading-snug">
            L&apos;essentiel sans saisie au centime : récurrents en
            fourchettes, recalage de solde et patrimoine.
          </p>
        </nav>
        <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button
            onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }}
            className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors"
            title="Se deconnecter"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>
      </aside>
    );
  }

  // Music mode
  return (
    <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40">
      <BrandHeader
        activeModule={activeModule}
        menuOpen={moduleMenuOpen}
        setMenuOpen={setModuleMenuOpen}
      />

      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {/* ── Mon matos — inventaire perso (top-level, hors groupe) ── */}
        <Link
          href="/music/matos"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
            pathname.startsWith("/music/matos")
              ? "bg-terra/10 text-terra-dark dark:text-terra-light font-semibold"
              : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
          }`}
        >
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
          Mon matos
        </Link>

        {/* ── Mixing live guides — fiches de mixage live (MR18 / M AIR Edit) ── */}
        <Link
          href={selectedBand ? `/music/mixing-guides?band=${selectedBand}` : "/music/mixing-guides"}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
            pathname.startsWith("/music/mixing-guides")
              ? "bg-terra/10 text-terra-dark dark:text-terra-light font-semibold"
              : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
          }`}
        >
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 6h11M17 6h4M3 12h4M10 12h11M3 18h11M17 18h4M14 4v4M7 10v4M14 16v4" /></svg>
          Mixing live guides
        </Link>

        {/* ── Mes Groupes — en-tête cliquable vers la page « Mes groupes » ── */}
        <Link
          href="/music"
          title="Voir tous mes groupes"
          className={`group mt-3 mb-0.5 flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
            pathname === "/music"
              ? "text-terra-dark dark:text-terra-light"
              : "text-text-muted/70 hover:text-foreground hover:bg-sand-dark/30 dark:hover:bg-white/5"
          }`}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider">Mes Groupes</span>
          <svg className="w-3 h-3 ml-auto opacity-40 group-hover:opacity-90 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </Link>
        {bands.map((b) => {
          const isSel = selectedBand === b.id;
          return (
            <button
              key={b.id}
              onClick={() => { setSelectedBand(b.id); router.push(`/music/groupes/${b.id}`); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all ${
                isSel
                  ? "bg-terra/12 text-terra-dark dark:text-terra-light font-semibold ring-1 ring-terra/30"
                  : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
              }`}
            >
              <span className={`text-base ${isSel ? "" : "opacity-60"}`}>🎸</span>
              <span className="truncate text-left">{b.name}</span>
            </button>
          );
        })}
        {/* Add band */}
        {!showAddBand ? (
          <button onClick={() => setShowAddBand(true)} className="w-full text-left px-3 py-2 rounded-xl text-sm text-terra hover:bg-terra/5 font-medium">
            + Nouveau groupe
          </button>
        ) : (
          <div className="px-1 py-1">
            <input
              autoFocus
              value={newBandName}
              onChange={(e) => setNewBandName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateBand()}
              placeholder="Nom du groupe..."
              className="w-full text-sm px-2.5 py-1.5 rounded-lg bg-background border border-border-default focus:border-terra focus:outline-none"
            />
            <div className="flex justify-end gap-1 mt-1.5">
              <button onClick={() => { setShowAddBand(false); setNewBandName(""); }} className="text-[11px] text-text-muted px-2 py-1 hover:text-foreground">Annuler</button>
              <button onClick={handleCreateBand} disabled={creating} className="text-[11px] text-white bg-terra px-2.5 py-1 rounded-md hover:bg-terra/90 disabled:opacity-50">
                {creating ? "..." : "Créer"}
              </button>
            </div>
          </div>
        )}

        {/* ── Gestion du groupe sélectionné ── */}
        {selectedBand && (
          <>
            <div className="mt-3 mb-1 px-3 py-1.5 flex items-center gap-2 rounded-lg bg-terra/[0.06]">
              <span className="w-1.5 h-1.5 rounded-full bg-terra shrink-0" />
              <p className="text-xs font-semibold text-foreground truncate">{selectedBandObj?.name}</p>
            </div>
            {musicSubPages.map((page) => {
              const href = getHref(selectedBand, page.key);
              const isActive = page.key === "detail"
                ? pathname === `/music/groupes/${selectedBand}`
                : pathname.startsWith(`/music/${page.key}`);

              return (
                <Link
                  key={page.key}
                  href={href}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] font-medium transition-all ${
                    isActive
                      ? "bg-terra/10 text-terra-dark dark:text-terra-light font-semibold"
                      : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"
                  }`}
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={page.icon} />
                  </svg>
                  {page.label}
                </Link>
              );
            })}
          </>
        )}
      </nav>
      {isAdmin && (
        <div className="px-3 pb-2">
          <Link href="/admin" className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${pathname === "/admin" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 font-semibold" : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground"}`}>
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
            Admin
          </Link>
        </div>
      )}
      <div className="px-3 py-3 border-t border-border-default flex items-center justify-between">
          <ChangelogButton />
          <button
            onClick={async () => { const supabase = createClient(); await supabase.auth.signOut(); router.push("/login"); router.refresh(); }}
            className="p-2 rounded-lg text-text-muted hover:text-negative hover:bg-negative/10 transition-colors"
            title="Se deconnecter"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
          </button>
        </div>

      <style jsx global>{`
        @keyframes ddIn { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }
      `}</style>
    </aside>
  );
}

/* ── Brand header (shared between budget + music modes) ──
   LOT 127 — Remplace l'ancien `ModuleSwitcher` par un en-tête avec :
   - "Gestic" en gros (titre principal)
   - Le nom du module en dessous (Budget / Music) dans une typo serif
     italique, un chouille plus petit
   - Une icône burger ☰ à droite, qui ouvre un mini-menu pour switcher
     de module
*/
/* Sous-onglets de la barre latérale : plus de trait vertical, une pastille
   d'icône teintée de la couleur du module. Elle s'anime au survol et se
   remplit sur la page active ; la hiérarchie tient au retrait. */
const CSS_SOUS_LIEN = `
.sb-sous { display: flex; align-items: center; gap: 10px; padding: 5px 8px 5px 5px; border-radius: 12px; font-size: 13px; font-weight: 500; transition: background-color .2s ease, color .2s ease; }
.sb-pastille {
  width: 26px; height: 26px; border-radius: 9px; display: grid; place-items: center; flex: none;
  color: var(--c); background: color-mix(in srgb, var(--c) 13%, transparent);
  transition: transform .25s cubic-bezier(.34,1.56,.64,1), background-color .2s ease, color .2s ease, box-shadow .25s ease;
}
.sb-sous:hover .sb-pastille { transform: scale(1.08); background: color-mix(in srgb, var(--c) 20%, transparent); }
.sb-sous[data-actif="oui"] .sb-pastille {
  background: var(--c); color: #fff;
  box-shadow: 0 5px 12px -5px color-mix(in srgb, var(--c) 75%, transparent), inset 0 1px 0 rgb(255 255 255 / .25);
}
.sb-sous[data-discret="oui"] .sb-pastille { background: transparent; box-shadow: inset 0 0 0 1.5px color-mix(in srgb, var(--c) 45%, transparent); }
@media (prefers-reduced-motion: reduce) { .sb-pastille { transition: none; } .sb-sous:hover .sb-pastille { transform: none; } }
`;

function SousLien({ href, actif, couleur, icone, discret = false, children }: {
  href: string;
  actif: boolean;
  couleur: string;
  icone: string;
  discret?: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      <style>{CSS_SOUS_LIEN}</style>
      <Link
        href={href}
        data-actif={actif ? "oui" : "non"}
        data-discret={discret ? "oui" : "non"}
        style={{ "--c": couleur } as React.CSSProperties}
        className={`sb-sous ${
          actif
            ? "bg-forest/10 text-forest-dark dark:text-forest-light font-semibold"
            : `${discret ? "text-text-muted opacity-80 hover:opacity-100" : "text-text-muted"} hover:bg-sand-dark/30 dark:hover:bg-white/5 hover:text-foreground`
        }`}
      >
        <span className="sb-pastille" aria-hidden>
          <svg className="w-[15px] h-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
            <path strokeLinecap="round" strokeLinejoin="round" d={icone} />
          </svg>
        </span>
        <span className="truncate">{children}</span>
      </Link>
    </>
  );
}

/* Sous-modules de Revenus (Intermittence, Clients) : icône au trait comme
   les onglets principaux, retrait conservé. */
function SousModule({ href, actif, icone, children }: {
  href: string;
  actif: boolean;
  icone: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
        actif
          ? "bg-forest/10 text-forest-dark dark:text-forest-light font-semibold"
          : "text-text-secondary hover:bg-sand-dark/30 dark:hover:bg-white/5"
      }`}
    >
      {icone}
      <span className="truncate">{children}</span>
    </Link>
  );
}

/** Clé de sol au trait, dessinée pour la grille 24 × 24 des icônes de navigation. */
function IconeCleDeSol() {
  return (
    <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12.2 3.2 13 19.6c.1 1.6-.8 2.5-2 2.4-.9-.1-1.4-.8-1.2-1.6" />
      <path d="M12.2 3.2c0-1.1.9-1.8 1.7-1.3 1 .6.9 2.3-.2 3.4-1.3 1.3-3.8 2.7-5.1 4.6-1.5 2.1-1.5 5 .1 6.7 1.6 1.7 4.8 1.9 6.5.5 1.7-1.3 1.8-4 .3-5.3-1.3-1.2-3.6-1-4.6.4-.7 1.1-.4 2.8.9 3.1.8.2 1.5-.2 1.7-.8" />
      <circle cx="10.55" cy="20.35" r="1.05" fill="currentColor" stroke="none" />
    </svg>
  );
}

function BrandHeader({
  activeModule,
  menuOpen,
  setMenuOpen,
}: {
  activeModule: "budget" | "music" | "famille" | "rando" | "light" | "voiture" | "velo" | "dispos" | "sante";
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}) {
  const allowed = useAccessibleModules();
  const canSwitch = allowed.size > 1;
  // REFONTE v2 — soulignage animé de "Gestic" : couleur gérée en WAAPI
  // (terra → forêt), le label texte du module a été retiré.
  const barRef = useRef<HTMLSpanElement>(null);
  function playUnderline() {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const bar = barRef.current;
    if (!bar) return;
    bar.getAnimations().forEach((a) => a.cancel());
    // Chorégraphie fluide : arrive de la gauche en grandissant → bounce
    // doux au bord droit → repart → sort par la droite en fondu. La
    // couleur glisse de terra cotta (début) vers vert forêt (fin), de
    // façon continue (interpolation WAAPI par keyframe). Easings par
    // segment pour un mouvement organique (pas de cassure).
    bar.animate(
      [
        { transform: "scaleX(0)", backgroundColor: "#C1694F", opacity: 0.85, offset: 0, easing: "cubic-bezier(0.33, 0, 0.2, 1)" },
        { transform: "scaleX(1)", backgroundColor: "#A86A52", opacity: 1, offset: 0.38, easing: "cubic-bezier(0.45, 0, 0.2, 1)" },
        { transform: "scaleX(0.9)", backgroundColor: "#7E6A54", opacity: 1, offset: 0.52, easing: "ease-in-out" },
        { transform: "scaleX(1)", backgroundColor: "#4C6A53", opacity: 1, offset: 0.66, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
        { transform: "scaleX(1) translateX(0)", backgroundColor: "#2D6A4F", opacity: 1, offset: 0.78, easing: "cubic-bezier(0.55, 0, 0.45, 1)" },
        { transform: "scaleX(1) translateX(114%)", backgroundColor: "#2D6A4F", opacity: 0, offset: 1 },
      ],
      { duration: 1250, fill: "both" },
    );
  }
  return (
    <div className="px-5 py-6 relative">
      {/* REFONTE v2 — Gestic seul (label module retiré), aligné verticalement
          avec l'icône burger (items-center), toujours collé à gauche. */}
      <div className="flex items-center justify-between gap-3 min-h-8">
        <div className="min-w-0">
          <div
            className="relative inline-block overflow-hidden pt-1.5 pb-1.5"
            onMouseEnter={playUnderline}
          >
            <h1 className="font-serif text-[22px] font-bold tracking-tight text-foreground leading-none">Gestic</h1>
            <span
              ref={barRef}
              aria-hidden
              className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-full origin-left pointer-events-none"
              style={{ transform: "scaleX(0)", opacity: 0 }}
            />
          </div>
        </div>
        {/* Cloche de notifications (visible dans TOUS les modules) + burger de
            switch module (masqué si le plan ne donne accès qu'à un module). */}
        <div className="flex items-center gap-0.5 shrink-0">
          <NotificationsBell />
          {canSwitch && (
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Changer de module"
              aria-expanded={menuOpen}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-text-muted hover:text-foreground hover:bg-sand-dark/30 dark:hover:bg-white/5 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* REFONTE v2 — Palette de commandes : raccourci clavier ⌘K uniquement
          (déclencheur géré globalement dans command-palette.tsx). Pas de
          bouton visible dans le sidebar, par choix produit. */}

      {/* Mini-menu de switch module */}
      {canSwitch && menuOpen && (
        <>
          {/* Backdrop click-out */}
          <button
            type="button"
            aria-label="Fermer"
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-3 top-[calc(100%-0.5rem)] z-50 w-44 rounded-xl border border-border-default bg-surface shadow-lg overflow-hidden animate-[ddIn_150ms_ease-out]">
            {allowed.has("budget") && (
            <Link
              href="/budget"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors ${
                activeModule === "budget"
                  ? "bg-forest/10 text-forest font-semibold"
                  : "text-text-secondary hover:bg-forest/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-forest" aria-hidden />
              <span className="font-serif italic">Budget</span>
            </Link>
            )}
            {allowed.has("music") && (
            <Link
              href="/music"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "music"
                  ? "bg-terra/10 text-terra font-semibold"
                  : "text-text-secondary hover:bg-terra/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-terra" aria-hidden />
              <span className="font-serif italic">Music</span>
            </Link>
            )}
            {/* LOT 217 — Famille */}
            {allowed.has("famille") && (
            <Link
              href="/famille"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "famille"
                  ? "bg-violet-500/10 text-violet-700 dark:text-violet-300 font-semibold"
                  : "text-text-secondary hover:bg-violet-500/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-violet-500" aria-hidden />
              <span className="font-serif italic">Famille</span>
            </Link>
            )}
            {/* Module Rando */}
            {allowed.has("rando") && (
            <Link
              href="/rando"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "rando"
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold"
                  : "text-text-secondary hover:bg-emerald-500/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden />
              <span className="font-serif italic">Rando</span>
            </Link>
            )}
            {/* REFONTE v2 — Gestic Light */}
            {allowed.has("light") && (
            <Link
              href="/light"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "light"
                  ? "bg-forest/10 text-forest dark:text-forest-light font-semibold"
                  : "text-text-secondary hover:bg-forest/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--color-forest-light)" }} aria-hidden />
              <span className="font-serif italic">Light</span>
            </Link>
            )}
            {/* Module Voiture */}
            {allowed.has("voiture") && (
            <Link
              href="/voiture"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "voiture"
                  ? "bg-sky-500/10 text-sky-700 dark:text-sky-300 font-semibold"
                  : "text-text-secondary hover:bg-sky-500/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-sky-500" aria-hidden />
              <span className="font-serif italic">Voiture</span>
            </Link>
            )}
            {/* Module Vélo */}
            {allowed.has("velo") && (
            <Link
              href="/velo"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "velo"
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold"
                  : "text-text-secondary hover:bg-emerald-500/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden />
              <span className="font-serif italic">Vélo</span>
            </Link>
            )}
            {/* Module Dispos */}
            {allowed.has("dispos") && (
            <Link
              href="/dispos"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "dispos"
                  ? "bg-forest/10 text-forest font-semibold"
                  : "text-text-secondary hover:bg-forest/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-forest" aria-hidden />
              <span className="font-serif italic">Dispos</span>
            </Link>
            )}
            {/* Module Santé */}
            {allowed.has("sante") && (
            <Link
              href="/sante"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors border-t border-border-default/60 ${
                activeModule === "sante"
                  ? "bg-rose-500/10 text-rose-700 dark:text-rose-300 font-semibold"
                  : "text-text-secondary hover:bg-rose-500/5 hover:text-foreground"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-rose-500" aria-hidden />
              <span className="font-serif italic">Santé</span>
            </Link>
            )}
          </div>
        </>
      )}
      <style jsx global>{`
        @keyframes ddIn { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }
      `}</style>
    </div>
  );
}
